import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';

export interface CTAButtonProps {
  text: string;
  link?: string;
  href?: string;
  isExternal?: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
}

export interface SharedCTAProps {
  eyebrow?: string;
  title: string | React.ReactNode;
  description: string;
  primaryButton?: CTAButtonProps;
  secondaryButton?: CTAButtonProps;
  className?: string;
  containerClassName?: string;
  maxWidth?: string;
}

export function SharedCTA({
  eyebrow = "CUSTOM ENGINEERING SOLUTIONS",
  title,
  description,
  primaryButton = {
    text: "Get a Free Quote",
    link: "/contact-us"
  },
  secondaryButton = {
    text: "Explore Services",
    link: "/services"
  },
  className,
  containerClassName,
  maxWidth = "max-w-6xl"
}: SharedCTAProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const renderButton = (btn: CTAButtonProps, isPrimary: boolean) => {
    const baseClasses = cn(
      "px-7 sm:px-8 py-3 h-[48px] sm:h-[50px] font-bold text-sm sm:text-base transition-all duration-200 inline-flex items-center justify-center gap-2 cursor-pointer select-none whitespace-nowrap shrink-0 group",
      isPrimary
        ? "cta-btn-primary rounded-full bg-white !text-brand-900 hover:bg-brand-50 shadow-xl hover:scale-105 active:scale-95"
        : "cta-btn-secondary rounded-full bg-white/10 hover:bg-white/20 border border-white/30 !text-white hover:border-white/60 hover:scale-105 backdrop-blur-sm active:scale-95"
    );

    const buttonStyle = isPrimary
      ? { color: 'var(--color-brand-900)', backgroundColor: '#ffffff' }
      : { color: '#ffffff' };

    const content = (
      <>
        <span
          className={cn('font-bold', isPrimary ? '!text-brand-900' : '!text-white')}
          style={{ color: isPrimary ? 'var(--color-brand-900)' : '#ffffff' }}
        >
          {btn.text}
        </span>
        {btn.icon ? (
          btn.icon
        ) : (
          <ArrowRight
            className={cn(
              "w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1",
              isPrimary ? '!text-brand-900' : '!text-white'
            )}
            style={{ color: isPrimary ? 'var(--color-brand-900)' : '#ffffff', stroke: 'currentColor' }}
          />
        )}
      </>
    );

    if (btn.link) {
      return (
        <Link key={btn.text} to={btn.link} className={baseClasses} style={buttonStyle} onClick={btn.onClick}>
          {content}
        </Link>
      );
    }

    if (btn.href) {
      return (
        <a
          key={btn.text}
          href={btn.href}
          target={btn.isExternal ? "_blank" : undefined}
          rel={btn.isExternal ? "noopener noreferrer" : undefined}
          className={baseClasses}
          style={buttonStyle}
          onClick={btn.onClick}
        >
          {content}
        </a>
      );
    }

    return (
      <button key={btn.text} type="button" onClick={btn.onClick} className={baseClasses} style={buttonStyle}>
        {content}
      </button>
    );
  };

  return (
    <section className={cn("py-12 sm:py-16 relative bg-transparent", containerClassName)}>
      <div className={cn("mx-auto px-4 sm:px-6 lg:px-8 relative z-10", maxWidth)}>
        <div
          className={cn(
            "cta-banner shared-cta-card w-full h-auto min-h-[350px] md:h-[390px] lg:h-[400px] flex flex-col justify-center items-center p-6 sm:p-10 lg:px-14 lg:py-10 rounded-3xl sm:rounded-[3rem] border relative overflow-hidden text-center shadow-2xl transition-all duration-300",
            isLight
              ? "bg-gradient-to-r from-brand-600 via-brand-700 to-indigo-800 border-brand-500/30 text-white shadow-[0_25px_60px_rgba(var(--app-primary-rgb),0.22)]"
              : "bg-gradient-to-r from-brand-600 via-brand-800 to-brand-950 border-brand-400/30 text-white shadow-[0_25px_60px_rgba(var(--app-primary-rgb),0.16)]",
            className
          )}
        >
          <div className="relative z-10 max-w-3xl w-full mx-auto flex flex-col items-center justify-center my-auto">
            {eyebrow && (
              <span
                className={cn(
                  "cta-eyebrow inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 border !text-white bg-white/15 border-white/25 backdrop-blur-sm"
                )}
                style={isLight ? { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.35)' } : undefined}
              >
                {eyebrow}
              </span>
            )}

            <h2
              className={cn(
                "text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-3 sm:mb-4 font-display leading-tight max-w-2xl mx-auto",
                isLight ? "!text-white text-white" : "text-white"
              )}
              style={isLight ? { color: '#ffffff' } : undefined}
            >
              {title}
            </h2>

            <p
              className={cn(
                "cta-desc text-sm sm:text-base leading-relaxed mb-6 sm:mb-8 max-w-2xl mx-auto font-normal",
                isLight ? "!text-[#e0e7ff] text-brand-100" : "text-brand-100"
              )}
              style={isLight ? { color: '#e0e7ff' } : undefined}
            >
              {description}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 shrink-0">
              {primaryButton && renderButton(primaryButton, true)}
              {secondaryButton && renderButton(secondaryButton, false)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SharedCTA;
